import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, IconButton, Skeleton, TableCell, TableRow, Typography } from '@mui/material';
import React from 'react';
import zeplinColor from 'theme/Color';
import { TaskData } from '../../types';
import { STATUS_CONFIG, TaskType } from '../../constants';
import { TaskRow } from '../TaskRow/TaskRow';
import { Task } from '../../../../../../../model/tasks.model';

type TasksTableSectionProps = {
  section: typeof STATUS_CONFIG[0];
  data: TaskData;
  loading: boolean;
  isOpen: boolean;
  onToggle: (sectionKey: string) => void;
  onTaskClick: (task: Task) => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  onStatusChange: () => void;
  onAddTask: (status: string) => void;
};

export const TasksTableSection = ({
  section,
  data,
  loading,
  isOpen,
  onToggle,
  onTaskClick,
  onEditTask,
  onDeleteTask,
  onStatusChange,
  onAddTask,
}: TasksTableSectionProps) => {
  const tasks = data.records || [];

  return (
    <React.Fragment key={section.key}>
      <TableRow
        sx={{
          '& td': { borderBottom: '2px solid #ddd', paddingLeft: '12px', height: '56px' },
          backgroundColor: '#fafafa',
        }}
      >
        <TableCell colSpan={4} sx={{ padding: '12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              aria-label='expand row'
              size='small'
              onClick={() => onToggle(section.key)}
              sx={{ padding: '4px' }}
            >
              {isOpen ? (
                <KeyboardArrowUpIcon sx={{ fontSize: '20px' }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
              )}
            </IconButton>
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: section.color,
                flexShrink: 0,
              }}
            />
            <Typography variant='heading3' sx={{ fontWeight: 500 }}>
              {section.label} ({tasks.length})
            </Typography>
          </Box>
        </TableCell>
      </TableRow>

      {loading ? (
        <>
          {[1, 2, 3].map((i) => (
            <TableRow key={`skeleton-${section.key}-${i}`}>
              <TableCell sx={{ padding: '16px 12px' }}>
                <Skeleton variant='text' width='60%' />
              </TableCell>
              <TableCell sx={{ padding: '16px 12px' }}>
                <Skeleton variant='circular' width={24} height={24} />
              </TableCell>
              <TableCell sx={{ padding: '16px 12px' }}>
                <Skeleton variant='text' width='40%' />
              </TableCell>
              <TableCell sx={{ padding: '16px 12px' }}>
                <Skeleton variant='rectangular' width={60} height={22} />
              </TableCell>
            </TableRow>
          ))}
        </>
      ) : (
        <>
          {isOpen && tasks.length > 0 && (
            <>
              {tasks.map((task) => (
                <TaskRow
                  key={task.id}
                  task={task}
                  onTaskClick={onTaskClick}
                  onEditTask={onEditTask}
                  onDeleteTask={onDeleteTask}
                  onStatusChange={onStatusChange}
                />
              ))}
              <TableRow>
                <TableCell colSpan={4} sx={{ padding: '12px 16px', borderBottom: 'none' }}>
                  <Box
                    onClick={() => onAddTask(section.apiStatus)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      color: zeplinColor.BasicBlack,
                      padding: '8px 0',
                      '&:hover': {
                        opacity: 0.7,
                      },
                    }}
                  >
                    <img src='/assets/add.svg' width={16} height={16} alt='Add' />
                    <Typography
                      variant='body2'
                      sx={{
                        color: zeplinColor.BasicBlack,
                        textTransform: 'none',
                        fontWeight: 400,
                      }}
                    >
                      Add Task
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </>
          )}
          {isOpen && tasks.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={4} sx={{ padding: '24px', textAlign: 'center' }}>
                <Typography variant='body2' color={zeplinColor.Neutral8}>
                  No tasks found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </>
      )}
    </React.Fragment>
  );
};
